import { onBeforeUnmount } from 'vue';
import { useDefaultDataLayer } from '../useDataLayer';

const THROTTLE_DELAY = 500;

interface PromotionItem {
  promotion_id?: string;
  promotion_name?: string;
  [key: string]: unknown;
}

interface SanitizedPromotionItem extends PromotionItem {
  promotion_id: string;
  promotion_name: string;
}

interface UsePromotionTrackingOptions {
  componentName?: string;
  disableThrottling?: boolean;
}

const sanitizePromotionItem = (item: PromotionItem = {}): SanitizedPromotionItem => {
  const fallbacks: PromotionItem = {
    promotion_id: '',
    promotion_name: '',
  };

  return Object.fromEntries(
    Object.entries({ ...fallbacks, ...item }).map(([key, value]) => [key, value ?? ''])
  ) as SanitizedPromotionItem;
};

export function usePromotionTracking({
  componentName = '',
  disableThrottling = false,
}: UsePromotionTrackingOptions = {}) {
  const { registerPromotionEvent } = useDefaultDataLayer();

  let pendingItems: SanitizedPromotionItem[] = [];
  let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    if (!pendingItems.length) {
      return;
    }

    const items = pendingItems;
    pendingItems = [];
    flushTimeoutId = null;

    registerPromotionEvent({
      event: 'view_promotion',
      component: componentName,
      ecommerce: {
        items,
      },
    });
  };

  const scheduleFlush = () => {
    if (flushTimeoutId) return;

    flushTimeoutId = setTimeout(flush, THROTTLE_DELAY);
  };

  const trackPromotionView = (promotionItem: PromotionItem = {}) => {
    pendingItems.push(sanitizePromotionItem(promotionItem));
    if (disableThrottling) flush();
    else scheduleFlush();
  };

  const trackPromotionSelect = (promotionItem: PromotionItem = {}) => {
    registerPromotionEvent({
      event: 'select_promotion',
      component: componentName,
      ecommerce: {
        items: [sanitizePromotionItem(promotionItem)],
      },
    });
  };

  if (!disableThrottling) {
    onBeforeUnmount(() => {
      if (flushTimeoutId) {
        clearTimeout(flushTimeoutId);
        flushTimeoutId = null;
      }

      flush();
    });
  }

  return {
    trackPromotionView,
    trackPromotionSelect,
  };
}
